import { Comment } from './comment.interface';
import { PolicyStatusDisplayKey } from './policies.interface';
import { TaskDetailsStakeholders } from './tasks.interface';

export enum ProcedureStatusDisplayKey {
  Pending = 'Pending',
  Approved = 'Approved',
  Changed = 'Changed',
  Blocked = 'Blocked',
  Removed = 'Removed',
}

export enum ProcedureStatusCode {
  PENDING_BLOCKED = 'Pending Blocked',
  PENDING_SELECTION = 'Pending Selection',
  PENDING_READY = 'pending ready for transition',
  APPROVED = 'Approved',
  REMOVED = 'Not relevant',
  CHANGED_ENABLED = 'Changed enabled',
  CHANGED_DISABLED = 'Changed disabled',
}

export enum ProcedureStatusEnum {
  PENDING_BLOCKED = 1,
  PENDING_SELECTION = 2,
  PENDING_READY = 3,
  APPROVED = 4,
  REMOVED = 5, //not relevant
  CHANGED_ENABLED = 6,
  CHANGED_DISABLED = 7, //blocked
}

export enum ProcedureStatusNames {
  Pending = 'Pending',
  Approved = 'Approved',
}

export interface ProcedureOwner {
  ownerID: string;
  ownerFullName: string;
  stakeHolderName?: string | null;
}

export interface ProcedureStatus {
  /** Stable technical key — prefer for lookups (Task 2531 T178). */
  codeName: ProcedureStatusCode;
  /** Canonical English display name. */
  statusName?: string;
  statusNameHe?: string;
  statusNameAr?: string;
  description: string;
  id: ProcedureStatusEnum;
}

export interface ProcedureItemStatus {
  codeName: number;
  statusName?: string;
  statusNameHe?: string;
  statusNameAr?: string;
  information?: string;
  informationHe?: string;
  informationAr?: string;
  mandatoryItemStatusOption: boolean;
  id: number;
}

export interface ProcedureDetails {
  procedureId: string;
  name: string;
}

export interface Procedure {
  procedureId: string;
  rowVersion?: string;
  name: string;
  statusCode: ProcedureStatusCode;
  statusDisplayKey: ProcedureStatusDisplayKey;
  statusId: ProcedureStatusEnum;
  statusInformationKey?: string | null;
  approvedItems: number;
  totalItems: number;
  activatedOn?: string | null;
  lastModified?: string | null;
  addedOn?: string | null;
  isLocked: boolean;
  canChangeStatus: boolean;
  ownerInfo?: ProcedureOwner | null;
  additionalStakeholdersCount?: number;
  isRelevant: boolean;
}

export interface ProcedureItemOwner {
  ownerName: string;
  ownerRole?: string | null;
  additionalStakeholdersCount?: number;
}

export interface ProcedureItemPrerequisite {
  itemId: string;
  name: string;
}

export interface ProcedureItemBase {
  itemId: string;
  name: string;
  currentStatus: string;
  statusId: number;
  statusInformationKey?: string | null;
  locked: boolean;
  canChangeStatus: boolean;
  mandatory: boolean;
  prerequisites: ProcedureItemPrerequisite[];
  owner?: ProcedureItemOwner | null;
}

export interface ProcedureSubProcedureItem extends ProcedureItemBase {
  originalProcedureStatus: ProcedureStatusDisplayKey;
  rowVersion?: string;
  skippable: boolean;
  canBeApproved: boolean;
  canRevertToPending: boolean;
  mandatory: boolean;
}

export interface ProcedureActionItem extends ProcedureItemBase {
  rowVersion?: string;
  skippable: boolean;
  canBeApproved: boolean;
  canRevertToPending: boolean;
}

export interface ProcedureItemsControlGroup {
  controlId: string;
  controlName: string;
  actions: ProcedureActionItem[];
}

export interface ProcedureItemsPolicyGroup {
  policyId: string;
  policyName: string;
  /** Icon next to the policy name; null for the no-policy group. */
  statusIcon: StatusIconType | null;
  /** System message whose "Tooltip" key holds the translated icon tooltip. */
  statusTooltipMessageId: string | null;
  /** English icon tooltip, used when the system message is missing. */
  statusTooltip: string | null;
  /** True when no action under the policy can be approved now; ignores the user's role. */
  approveAllDisabled: boolean;
  canChangeStatus: boolean;
  controls: ProcedureItemsControlGroup[];
}

export interface ProcedureItemsResponse {
  canChangeStatus: boolean;
  subProcedures: ProcedureSubProcedureItem[];
  actionsByPolicy: ProcedureItemsPolicyGroup[];
  approveAllActionsDisabled: boolean;
  approveAllSubProceduresDisabled: boolean;
}

export interface ProcedureItemUpdateResponse {
  item: ProcedureSubProcedureItem | ProcedureActionItem;
  systemMessageId?: string | null;
}

export enum StatusIconType {
  Info = 'Info',
  Completed = 'Completed',
  Warning = 'Warning',
  Error = 'Error',
  InProcess = 'InProcess',
}

export interface ProcedureDetailsAction {
  actionId: string;
  actionName: string;
  statusDisplayKey?: string;
}

export interface ProcedureDetailsSubProcedure {
  subProcedureId: string;
  subProcedureName: string;
  statusDisplayKey?: string;
}

export interface ProcedureDetailsReference {
  policyId: string;
  policyName: string;
  statusDisplayKey?: PolicyStatusDisplayKey;
}

export interface ProcedureDetailsChangelogEntry {
  eventTypeName: string;
  changeDate: string;
}

export interface ProcedureDetailsDto {
  procedureId: string;
  procedureName: string;
  statusCode: ProcedureStatusDisplayKey;
  statusDisplayKey: ProcedureStatusDisplayKey;
  approvedSubProceduresCount: number;
  totalSubProceduresCount: number;
  approvedMandatoryActionsCount: number;
  totalMandatoryActionsCount: number;
  approvedActionsCount: number;
  totalActionsCount: number;
  descriptionText?: string;
  instructionsText?: string;
  actions?: ProcedureDetailsAction[];
  subProcedures?: ProcedureDetailsSubProcedure[];
  references?: ProcedureDetailsReference[];
  stakeholders?: TaskDetailsStakeholders;
  changelog?: ProcedureDetailsChangelogEntry[];
  comments?: Comment[];
}

export interface ActionDetailsRemediationSolution {
  solutionName: string;
  statusIconType: StatusIconType;
  isActiveSolution: boolean;
  solutionId: string;
  assets: ActionDetailsRemediationSolutionAsset[];
}

export interface ActionDetailsRemediationSolutionAsset {
  assetCount: number;
  assetTypeDisplayName: string;
  assetTypeId: string;
}

export interface ActionDetailsConnectedVia {
  policy?: {
    objectId: string;
    displayName: string;
    statusIconType: StatusIconType;
  };
  control?: {
    objectId: string;
    displayName: string;
    statusIconType: StatusIconType;
  };
}

export interface ActionDetailsAffectedSystem {
  assetTypeId: string;
  assetTypeDisplayName: string;
  assetCount: number;
}

export interface ActionDetailsParameter {
  parameterId: string;
  parameterName: string;
  parameterType: 'Static' | 'Dynamic';
  staticValue?: string | null;
  dynamicSourceType?: 'Param' | 'Output' | null;
  dynamicSourceParameterName?: string | null;
  dynamicSourceActionName?: string | null;
  dynamicSourceActionId?: string | null;
  value?: string | null;
}

export interface ActionDetailsPrerequisiteAction {
  actionId: string;
  actionName: string;
  statusIconType: StatusIconType;
}

export interface ActionDetailsDependencyAction {
  actionId: string;
  actionName: string;
  statusIconType: StatusIconType;
}

export interface ActionDetailsChangelogEntry {
  eventTypeName: string;
  changeDate: string;
}

export interface ActionDetailsDto {
  activeProcedureItemId: string;
  actionName: string;
  mandatoryFlag: boolean;
  statusCode: string;
  statusDisplayKey: string;
  descriptionText?: string;
  remediationSolution?: ActionDetailsRemediationSolution;
  remediationSolutions?: ActionDetailsRemediationSolution[];
  connectedVia?: ActionDetailsConnectedVia;
  affectedSystems?: ActionDetailsAffectedSystem[];
  parameters?: ActionDetailsParameter[];
  prerequisiteActions?: ActionDetailsPrerequisiteAction[];
  dependencyActions?: ActionDetailsDependencyAction[];
  changelog?: ActionDetailsChangelogEntry[];
  comments?: Comment[];
}

export enum QuickApprovalScope {
  SubProcedures = 1,
  AllActions = 2,
  PolicyActions = 3,
}

export interface QuickApprovalResponse {
  totalCount: number;
  approvedCount: number;
  approvedNowCount: number;
}
